import { useState, useEffect } from 'react';
import type { OrderWithItems } from '@/store/seller';

interface TimeoutTimerProps {
  order: OrderWithItems;
  className?: string;
}

export function TimeoutTimer({ order, className = '' }: TimeoutTimerProps) {
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [isOverdue, setIsOverdue] = useState(false);

  useEffect(() => {
    // 타임아웃이 필요한 상태인지 확인
    const needsTimeout = 
      (order.status === 'PENDING' && order.payment_status === 'PAID') ||
      (order.status === 'ACCEPTED' && order.order_type !== 'RESERVATION_DISCOUNT');

    if (!needsTimeout) {
      setTimeLeft('');
      return;
    }

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      let targetTime: number;

      if (order.status === 'PENDING') {
        // PENDING 상태 타임아웃
        const createdAt = new Date(order.created_at).getTime();
        
        switch (order.order_type) {
          case 'DEADLINE_DISCOUNT':
            targetTime = createdAt + 30 * 60 * 1000; // 30분
            break;
          case 'RESERVATION_DISCOUNT':
            const createdHour = new Date(order.created_at).getHours();
            if (createdHour >= 0 && createdHour < 6) {
              // 0-6시 주문은 오전 9시까지
              const targetDate = new Date(order.created_at);
              targetDate.setHours(9, 0, 0, 0);
              targetTime = targetDate.getTime();
            } else {
              targetTime = createdAt + 8 * 60 * 60 * 1000; // 8시간
            }
            break;
          default: // REGULAR_ORDER
            targetTime = createdAt + 30 * 60 * 1000; // 30분
            break;
        }
      } else {
        // ACCEPTED 상태 타임아웃
        const acceptedAt = order.accepted_at 
          ? new Date(order.accepted_at).getTime()
          : new Date().getTime();
        
        if (order.order_type === 'DEADLINE_DISCOUNT') {
          targetTime = acceptedAt + 60 * 60 * 1000; // 1시간
        } else {
          targetTime = acceptedAt + 2 * 60 * 60 * 1000; // 2시간
        }
      }

      const diff = targetTime - now;
      
      if (diff <= 0) {
        setIsOverdue(true);
        setTimeLeft('시간 초과');
        return;
      }

      setIsOverdue(false);
      
      const minutes = Math.floor(diff / (1000 * 60));
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;

      // 5분 이하면 긴급 표시
      setIsUrgent(minutes <= 5);

      if (hours > 0) {
        setTimeLeft(`${hours}시간 ${remainingMinutes}분 남음`);
      } else {
        setTimeLeft(`${remainingMinutes}분 남음`);
      }
    };

    // 초기 계산
    calculateTimeLeft();

    // 10초마다 업데이트
    const interval = setInterval(calculateTimeLeft, 10000);

    return () => clearInterval(interval);
  }, [order]);

  if (!timeLeft) return null;

  const getTimerColor = () => {
    if (isOverdue) return 'text-red-600 bg-red-50';
    if (isUrgent) return 'text-orange-600 bg-orange-50';
    return 'text-blue-600 bg-blue-50';
  };

  const getStatusText = () => {
    if (order.status === 'PENDING') {
      return '수락 마감';
    } else if (order.status === 'ACCEPTED') {
      return '준비 마감';
    }
    return '';
  };

  return (
    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getTimerColor()} ${className}`}>
      {isUrgent && !isOverdue && (
        <span className="animate-pulse">⏰</span>
      )}
      {isOverdue && (
        <span>⚠️</span>
      )}
      <span>{getStatusText()}</span>
      <span className="font-bold">{timeLeft}</span>
    </div>
  );
}